import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { useLocation } from 'react-router-dom';

const Cursor = () => {
    const cursorRef = useRef(null);
    const followerRef = useRef(null);
    const [isHovering, setIsHovering] = useState(false);
    const location = useLocation();

    useEffect(() => {
        // Disable on mobile/touch devices
        const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const isMobile = window.matchMedia('(max-width: 768px)').matches;

        if (isTouch || isMobile) return;

        // Hide default cursor
        document.body.style.cursor = 'none';

        const cursor = cursorRef.current;
        const follower = followerRef.current;

        if (!cursor || !follower) return;

        const onMouseMove = (e) => {
            gsap.to(cursor, {
                x: e.clientX,
                y: e.clientY,
                duration: 0.1,
                ease: 'power2.out'
            });
            gsap.to(follower, {
                x: e.clientX,
                y: e.clientY,
                duration: 0.5,
                ease: 'power2.out'
            });
        };

        const onMouseEnter = () => setIsHovering(true);
        const onMouseLeave = () => setIsHovering(false);

        // Add event listeners for clickable elements
        const addListeners = () => {
            const clickables = document.querySelectorAll('a, button, .cursor-pointer');
            clickables.forEach((el) => {
                el.addEventListener('mouseenter', onMouseEnter);
                el.addEventListener('mouseleave', onMouseLeave);
            });
        };

        const removeListeners = () => {
            const clickables = document.querySelectorAll('a, button, .cursor-pointer');
            clickables.forEach((el) => {
                el.removeEventListener('mouseenter', onMouseEnter);
                el.removeEventListener('mouseleave', onMouseLeave);
            });
        };

        window.addEventListener('mousemove', onMouseMove);
        addListeners();

        // Re-add listeners on route change
        const observer = new MutationObserver(addListeners);
        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            document.body.style.cursor = 'auto';
            window.removeEventListener('mousemove', onMouseMove);
            removeListeners();
            observer.disconnect();
        };
    }, [location]);

    useEffect(() => {
        const cursor = cursorRef.current;
        const follower = followerRef.current;

        if (isHovering) {
            gsap.to(cursor, { scale: 0, duration: 0.3 });
            gsap.to(follower, {
                scale: 3,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                mixBlendMode: 'difference',
                duration: 0.3
            });
        } else {
            gsap.to(cursor, { scale: 1, duration: 0.3 });
            gsap.to(follower, {
                scale: 1,
                backgroundColor: 'transparent',
                mixBlendMode: 'normal',
                duration: 0.3
            });
        }
    }, [isHovering]);

    return (
        <>
            <div
                ref={cursorRef}
                className="hidden md:block fixed top-0 left-0 w-2 h-2 bg-white rounded-full pointer-events-none z-[9999] -translate-x-1/2 -translate-y-1/2 mix-blend-difference"
            />
            <div
                ref={followerRef}
                className="hidden md:block fixed top-0 left-0 w-8 h-8 border border-white rounded-full pointer-events-none z-[9998] -translate-x-1/2 -translate-y-1/2 mix-blend-difference"
            />
        </>
    );
};

export default Cursor;
